/**
 * Estatísticas da partida por atleta (item 4.5), contadas a partir das jogadas de cada tick. Servem para a tela
 * de resultado e para a nota de partida (GDD, seção 6; fórmula em `packages/shared/src/rating.ts`).
 */
import { RATING, type MatchStatLine } from "@besenliga/shared";
import type { BallsState, PlayEvent } from "./balls.js";
import type { AthleteState, Team } from "./flight.js";

export type AthleteMatchStats = MatchStatLine;

export function emptyStats(): AthleteMatchStats {
  return {
    gols: 0,
    arremessos: 0,
    passes: 0,
    passesCertos: 0,
    roubos: 0,
    defesas: 0,
    rebatidas: 0,
    petardosAcertados: 0,
    atordoado: 0,
    capturas: 0,
    faltas: 0,
    quedas: 0,
    faiscaTicks: 0,
  };
}

/** Último passe ainda no ar: se um companheiro pegar a bola, conta como passe certo. */
export interface PendingPass {
  from: string;
  team: Team;
}

/**
 * Soma as jogadas do tick nas estatísticas. Altera `stats` e devolve o passe pendente atualizado.
 */
export function recordStats(
  stats: Record<string, AthleteMatchStats>,
  plays: readonly PlayEvent[],
  athletes: readonly AthleteState[],
  balls: BallsState,
  pending: PendingPass | null,
): PendingPass | null {
  const byId = new Map(athletes.map((a) => [a.id, a]));
  const line = (id: string | undefined) => {
    if (!id || !byId.has(id)) return null;
    return (stats[id] ??= emptyStats());
  };
  let pass = pending;
  for (const p of plays) {
    switch (p.kind) {
      case "gol": {
        // Gol de quem tocou por último, se for do time que marcou (desvio de adversário não conta para ele).
        const a = byId.get(p.athlete);
        const s = line(p.athlete);
        if (a && s && a.team === p.team) s.gols += 1;
        pass = null;
        break;
      }
      case "arremesso": {
        const s = line(p.athlete);
        if (s) s.arremessos += 1;
        pass = null;
        break;
      }
      case "passe": {
        const a = byId.get(p.athlete);
        const s = line(p.athlete);
        if (s) s.passes += 1;
        pass = a ? { from: a.id, team: a.team } : null;
        break;
      }
      case "posse": {
        const a = byId.get(p.athlete);
        if (pass && a && a.team === pass.team && a.id !== pass.from) {
          const s = line(pass.from);
          if (s) s.passesCertos += 1;
        }
        pass = null;
        break;
      }
      case "roubo": {
        const s = line(p.other);
        if (s) s.roubos += 1;
        pass = null;
        break;
      }
      case "defesa": {
        const s = line(p.athlete);
        if (s) s.defesas += 1;
        break;
      }
      case "rebatida": {
        const s = line(p.athlete);
        if (s) s.rebatidas += 1;
        break;
      }
      case "atordoado": {
        const s = line(p.athlete);
        if (s) s.atordoado += 1;
        const by = byId.get(p.other ?? "");
        const victim = byId.get(p.athlete);
        const hitter = line(p.other);
        if (hitter && by && victim && by.team !== victim.team) hitter.petardosAcertados += 1;
        break;
      }
      case "captura": {
        const s = line(p.athlete);
        if (s) s.capturas += 1;
        break;
      }
      case "falta": {
        const s = line(p.athlete);
        if (s) s.faltas += 1;
        break;
      }
      case "queda": {
        const s = line(p.athlete);
        if (s) s.quedas += 1;
        break;
      }
      default:
        break;
    }
  }
  // Tempo do caçador perto da Faísca.
  const fa = balls.faisca;
  if (fa.active) {
    const r2 = RATING.sparkNearMeters * RATING.sparkNearMeters;
    for (const a of athletes) {
      if (a.role !== "cacador") continue;
      const dx = a.position.x - fa.position.x;
      const dy = a.position.y - fa.position.y;
      const dz = a.position.z - fa.position.z;
      const s = line(a.id);
      if (s && dx * dx + dy * dy + dz * dz < r2) s.faiscaTicks += 1;
    }
  }
  return pass;
}
